<script setup lang="ts">
import { computed, onScopeDispose, ref, watch } from 'vue';
import Button from 'primevue/button';
import parecerApi from '@/api/modules/parecerApi';
import BaseIcon from '@/components/base/BaseIcon.vue';

type PdfButtonState = 'idle' | 'polling' | 'ready' | 'failed' | 'timed_out';

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 2 * 60_000;

const props = defineProps<{
  parecerId: string | null | undefined;
  isFinalized: boolean;
}>();


const state = ref<PdfButtonState>('idle');
const signedUrl = ref<string | null>(null);
const errorMessage = ref<string | null>(null);

let pollInterval: ReturnType<typeof setInterval> | null = null;
let pollTimeout: ReturnType<typeof setTimeout> | null = null;
let pollRun = 0;

const isVisible = computed(() => Boolean(props.parecerId && props.isFinalized));

const label = computed(() => {
  switch (state.value) {
    case 'ready':
      return 'Ver PDF';
    case 'failed':
      return 'PDF indisponível';
    case 'timed_out':
      return 'Tentar novamente';
    default:
      return 'Gerando PDF';
  }
});

const icon = computed(() => {
  switch (state.value) {
    case 'ready':
      return 'picture_as_pdf';
    case 'failed':
      return 'error_outline';
    case 'timed_out':
      return 'refresh';
    default:
      return 'progress_activity';
  }
});

const tooltip = computed(() => {
  if (state.value === 'failed') {
    return errorMessage.value || 'Não foi possível gerar o PDF deste parecer.';
  }
  if (state.value === 'timed_out') {
    return 'O PDF ainda não ficou pronto. Você pode tentar novamente.';
  }
  return undefined;
});

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
  if (pollTimeout) {
    clearTimeout(pollTimeout);
    pollTimeout = null;
  }
};

const resetPolling = () => {
  stopPolling();
  pollRun += 1;
  state.value = 'idle';
  signedUrl.value = null;
  errorMessage.value = null;
};

const markTimedOut = () => {
  stopPolling();
  state.value = 'timed_out';
  errorMessage.value = null;
};

const getHttpStatus = (error: unknown): number | null => {
  if (!error || typeof error !== 'object' || !('status' in error)) return null;
  return typeof error.status === 'number' ? error.status : null;
};

const pollPdfStatus = async (run: number, parecerId: string) => {
  if (run !== pollRun || state.value !== 'polling') return;
  try {
    const result = await parecerApi.getPdfReportStatus(parecerId);
    if (run !== pollRun || state.value !== 'polling') return;

    if (result.status === 'completed') {
      if (!result.signedUrl) {
        stopPolling();
        state.value = 'failed';
        errorMessage.value = 'O PDF foi concluído, mas o link não está disponível.';
        return;
      }

      stopPolling();
      signedUrl.value = result.signedUrl;
      errorMessage.value = null;
      state.value = 'ready';
      return;
    }

    if (result.status === 'failed') {
      stopPolling();
      state.value = 'failed';
      errorMessage.value = result.errorMessage;
    }
  } catch (error: unknown) {
    if (run !== pollRun || state.value !== 'polling') return;

    const httpStatus = getHttpStatus(error);
    if (httpStatus !== null && httpStatus >= 400 && httpStatus < 500) {
      stopPolling();
      state.value = 'failed';
      errorMessage.value = httpStatus === 401 || httpStatus === 403
        ? 'Você não tem permissão para acessar este PDF.'
        : 'Não foi possível localizar o PDF deste parecer.';
    }
  }
};

const startPolling = () => {
  resetPolling();
  if (!props.parecerId || !props.isFinalized) return;

  state.value = 'polling';
  const run = pollRun;
  const parecerId = props.parecerId;

  void pollPdfStatus(run, parecerId);
  pollInterval = setInterval(() => {
    void pollPdfStatus(run, parecerId);
  }, POLL_INTERVAL_MS);
  pollTimeout = setTimeout(() => {
    if (run === pollRun && state.value === 'polling') {
      markTimedOut();
    }
  }, POLL_TIMEOUT_MS);
};

const handleClick = () => {
  if (state.value === 'ready' && signedUrl.value) {
    window.open(signedUrl.value, '_blank', 'noopener,noreferrer');
    return;
  }

  if (state.value === 'timed_out') {
    startPolling();
  }
};

watch(
  () => [props.parecerId, props.isFinalized] as const,
  () => startPolling(),
  { immediate: true },
);

onScopeDispose(resetPolling);
</script>

<template>
  <span v-if="isVisible" class="inline-flex" :title="tooltip" aria-live="polite">
    <Button
      :label="label"
      severity="secondary"
      outlined
      size="small"
      class="!h-8 !text-xs"
      :disabled="state === 'polling' || state === 'failed'"
      @click="handleClick"
    >
      <template #icon>
        <BaseIcon :class="['text-sm', { 'animate-spin': state === 'polling' }]">
          {{ icon }}
        </BaseIcon>
      </template>
    </Button>
  </span>
</template>
